/**
 * ObjectRenderer: dispatches a single LayoutObject to the correct shape
 * component based on its `type`.
 *
 * CRITICAL performance pattern: this component subscribes to the editor
 * store via a selector scoped to just THIS object's id
 * (`state.objects.find(o => o.id === id)`), not the whole `objects` array.
 * Zustand's selector subscriptions mean only the shape whose own object
 * actually changed will re-render — dragging one table does not re-render
 * the other twenty-nine. This is the difference between a canvas that
 * stays smooth at 30+ objects and one that lags on every drag frame.
 */

import { useEditorStore } from '../../store/useEditorStore';
import type Konva from 'konva';
import type { ObjectCatalogEntry } from '../../domain/types';
import { RoundTable } from './shapes/RoundTable';
import { RectTable } from './shapes/RectTable';
import { DanceFloor } from './shapes/DanceFloor';
import { FeatureZone } from './shapes/FeatureZone';
import { TextNote } from './shapes/TextNote';
import { StandaloneChair } from './shapes/StandaloneChair';
import { Arbor } from './shapes/Arbor';

interface ObjectRendererProps {
  key?: React.Key;
  objectId: string;
  catalog: ObjectCatalogEntry[];
  /** The room's current VIEW rotation in degrees (0 = standard/portrait,
   * 90 = the default landscape view) — see PlannerCanvas's own rotation
   * comments. Passed down so each shape's label can counter-rotate
   * against this specifically, keeping table numbers readable regardless
   * of which way the room is currently being viewed, WITHOUT changing
   * how a manually-rotated object's label behaves relative to that
   * object (it still rotates together with the table, exactly as
   * before) — only the room-view component of the rotation is canceled
   * out for labels, not the object's own rotation. */
  roomRotationDegrees: number;
}

export function ObjectRenderer({ objectId, catalog, roomRotationDegrees }: ObjectRendererProps) {
  // Narrow selector: this component only re-renders when THIS object
  // changes, not when any other object in the layout changes.
  const object = useEditorStore((state) => state.objects.find((o) => o.id === objectId));
  const isSelected = useEditorStore((state) => state.selectedIds.includes(objectId));
  const selectObject = useEditorStore((state) => state.selectObject);
  const moveObjectWithSelection = useEditorStore((state) => state.moveObjectWithSelection);

  if (!object) return null;

  const catalogEntry = catalog.find((c) => c.itemType === object.type);
  if (!catalogEntry) return null;

  // Konva click events bubble from the shape up to the Stage. PlannerCanvas's
  // stage-level handler is what actually implements shift-click multi-select
  // (it's the only place with direct access to the raw browser event's
  // shiftKey). So when shift IS held, this handler deliberately does
  // nothing and lets that bubbled event do the work — calling
  // selectObject() here too would replace the whole selection with just
  // this object first, then the stage handler's toggle would immediately
  // remove it again, since it would now read as "already selected."
  function handleSelect(e?: Konva.KonvaEventObject<Event>) {
    const nativeEvent = e?.evt as MouseEvent | undefined;
    if (nativeEvent?.shiftKey) return;
    selectObject(objectId);
  }

  function handleDragEnd(x: number, y: number) {
    // moveObjectWithSelection (see useEditorStore.ts) moves the whole
    // multi-selection together by the same delta, committed as one
    // history entry. KNOWN SIMPLIFICATION: only the actively-dragged
    // shape moves smoothly frame-by-frame (Konva's native draggable
    // handles that); the rest of the selection visually snaps to its
    // new position once the drag ends, rather than moving live in real
    // time alongside it. This was a deliberate choice — live multi-node
    // dragging requires direct Konva manipulation outside React's render
    // cycle for every selected shape, which is meaningfully higher-risk
    // to get right without the ability to test real mouse interaction.
    // The end result (group moves together, correctly, every time) is
    // identical either way; only the in-progress animation differs.
    moveObjectWithSelection(objectId, x, y);
  }

  switch (object.type) {
    case 'round_table':
      return (
        <RoundTable
          object={object}
          isSelected={isSelected}
          onSelect={handleSelect}
          onDragEnd={handleDragEnd}
          roomRotationDegrees={roomRotationDegrees}
        />
      );

    case 'rect_table_6':
    case 'rect_table_8':
    case 'head_table':
    case 'sweetheart_table':
      return (
        <RectTable
          object={object}
          catalogEntry={catalogEntry}
          isSelected={isSelected}
          onSelect={handleSelect}
          onDragEnd={handleDragEnd}
          roomRotationDegrees={roomRotationDegrees}
        />
      );

    case 'dance_floor_15':
    case 'dance_floor_18':
      return (
        <DanceFloor
          object={object}
          catalogEntry={catalogEntry}
          isSelected={isSelected}
          onSelect={handleSelect}
          onDragEnd={handleDragEnd}
          roomRotationDegrees={roomRotationDegrees}
        />
      );

    case 'dj_booth':
    case 'cake_table':
    case 'gift_table':
    case 'dessert_station':
    case 'donut_wall':
    case 'photo_booth':
    case 'cocktail_table':
      return (
        <FeatureZone
          object={object}
          catalogEntry={catalogEntry}
          isSelected={isSelected}
          onSelect={handleSelect}
          onDragEnd={handleDragEnd}
          roomRotationDegrees={roomRotationDegrees}
        />
      );

    case 'standalone_chair':
      return (
        <StandaloneChair
          object={object}
          catalogEntry={catalogEntry}
          isSelected={isSelected}
          onSelect={handleSelect}
          onDragEnd={handleDragEnd}
          roomRotationDegrees={roomRotationDegrees}
        />
      );
    case 'arbor':
      return (
        <Arbor
          object={object}
          catalogEntry={catalogEntry}
          isSelected={isSelected}
          onSelect={handleSelect}
          onDragEnd={handleDragEnd}
          roomRotationDegrees={roomRotationDegrees}
        />
      );
    case 'text_note':
      return (
        <TextNote
          object={object}
          catalogEntry={catalogEntry}
          isSelected={isSelected}
          onSelect={handleSelect}
          onDragEnd={handleDragEnd}
          roomRotationDegrees={roomRotationDegrees}
        />
      );

    default:
      return null;
  }
}
